/**
 * @zendir/ui - OrbitCard Component
 *
 * Enterprise-grade orbital parameters display card.
 *
 * AstroUXDS Compliance:
 * - Orbit type colors use Zendir-Astro data viz palette (categorical, not status)
 * - Tabular numbers for orbital data display
 * - Sentence-case labels per Tier 3 compliance
 *
 * Features:
 * - Full null-safety with graceful fallbacks
 * - WCAG 2.1 AA accessibility
 * - Computed orbital parameters (period, velocity)
 * - Orbit type classification with Zendir-branded color coding
 * - Loading and empty states
 */

import React, { memo, useMemo } from "react";
import type { SpacecraftPosition } from "../types";
import { estimateOrbitalPeriod, estimateOrbitalVelocity } from "../types";
import { useTheme } from "../theme";
import { Icon } from "../core/display/Icon";
import { AstroIcon } from "../core/display/AstroIcon";
import { CardHeader } from "../core/display/CardHeader";
import { useCompactMode } from "../hooks";
import {
  formatAltitude,
  formatVelocity,
  formatDuration,
  formatCoordinate,
  formatDegrees,
  formatTime,
  classNames,
  safeAccentText,
} from "../utils";

export interface OrbitCardProps {
  /** Spacecraft position data */
  spacecraft?: SpacecraftPosition;
  /** Show computed orbital parameters (period, velocity) */
  showComputed?: boolean;
  /** Loading state */
  loading?: boolean;
  /** Custom className */
  className?: string;
  /** Enable compact mode - shows condensed view that expands on hover/click */
  compact?: boolean;
  /** Start expanded when in compact mode (pinned state) */
  defaultExpanded?: boolean;
  /** Callback when pin state changes in compact mode */
  onPinChange?: (isPinned: boolean) => void;
}

/**
 * Orbit type information with color and description
 */
interface OrbitTypeInfo {
  color: string;
  description: string;
}

/**
 * Get orbit type info with Zendir-branded colors
 * Uses AstroUXDS data visualization palette for orbit classification
 */
function getOrbitTypeInfo(orbitType: string | undefined): OrbitTypeInfo {
  // Orbit type colors using Zendir-Astro palette
  // Non-status colors since orbit type is categorical, not status
  const types: Record<string, OrbitTypeInfo> = {
    LEO: { color: "#3E3CFF", description: "Low Earth Orbit (200-2,000 km)" }, // Zendir Electric
    MEO: {
      color: "#3548C0",
      description: "Medium Earth Orbit (2,000-35,786 km)",
    }, // Zendir Prussian Blue Light
    GEO: { color: "#9D70FF", description: "Geostationary Orbit (~35,786 km)" }, // Zendir Purple
    HEO: { color: "#1B2DA0", description: "Highly Elliptical Orbit" }, // Zendir Prussian Blue
    SSO: { color: "#B48DFF", description: "Sun-Synchronous Orbit" }, // Zendir Purple Light
    POLAR: { color: "#6B69FF", description: "Polar Orbit" }, // Zendir Electric Light
    MOLNIYA: { color: "#C9A8FF", description: "Molniya Orbit (12h period)" }, // Zendir Purple Lighter
  };

  return (
    types[orbitType?.toUpperCase() ?? ""] ?? {
      color: "#a4abb6",
      description: "Unknown orbit type",
    }
  );
}

/**
 * OrbitCard - Displays spacecraft orbital parameters
 *
 * @example
 * ```tsx
 * <OrbitCard
 *   spacecraft={spacecraftData}
 *   showComputed
 * />
 * ```
 */
export const OrbitCard = memo(function OrbitCard({
  spacecraft,
  showComputed = true,
  loading = false,
  className = "",
  compact = false,
  defaultExpanded = false,
  onPinChange,
}: OrbitCardProps): React.ReactElement {
  const { tokens, theme, prefersReducedMotion } = useTheme();
  const isTransparentTheme =
    theme === "transparent" ||
    theme === "transparent-bold" ||
    theme === "transparent-minimal";
  const isBoldVariant = theme === "transparent-bold";
  const isMinimalVariant = theme === "transparent-minimal";
  const useGlassMode = isBoldVariant || isMinimalVariant;

  // Glass accent colors for bold/minimal themes (matching Container/AstroChart)
  const glassAccentColor = tokens.colors.accent.primary;
  const glassAccentMuted = `${glassAccentColor}66`;

  // Use transparent background with blur for transparent themes (matching Timeline)
  const cardBg = isTransparentTheme
    ? "transparent"
    : tokens.colors.background.surface;
  const cardGlass = isTransparentTheme
    ? {
        backdropFilter: "blur(12px)" as const,
        WebkitBackdropFilter: "blur(12px)" as const,
      }
    : {};

  // Glass mode container style (matching Container/AstroChart for bold/minimal)
  const glassContainerStyle: React.CSSProperties = useGlassMode
    ? {
        backgroundColor: "rgba(10, 15, 25, 0.35)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        border: `1px solid ${glassAccentMuted}`,
        borderTop: isBoldVariant
          ? `2px solid ${glassAccentColor}`
          : `1px solid ${glassAccentColor}`,
      }
    : {};

  // Compact mode state management
  const { expanded, isPinned, handlers, transitionDuration } = useCompactMode({
    compact,
    defaultExpanded,
    onPinChange,
  });

  // Computed orbital parameters
  const orbitInfo = useMemo(
    () => getOrbitTypeInfo(spacecraft?.orbitType),
    [spacecraft?.orbitType],
  );

  const computedPeriod = useMemo(() => {
    if (!spacecraft?.altitude) return null;
    try {
      return estimateOrbitalPeriod(spacecraft.altitude);
    } catch {
      return null;
    }
  }, [spacecraft?.altitude]);

  const computedVelocity = useMemo(() => {
    if (!spacecraft?.altitude) return null;
    try {
      return estimateOrbitalVelocity(spacecraft.altitude);
    } catch {
      return null;
    }
  }, [spacecraft?.altitude]);

  // Loading state
  if (loading) {
    return (
      <div
        className={classNames("zendir-orbit-card", "loading", className)}
        role="article"
        aria-busy="true"
        aria-label="Loading orbit data"
        style={{
          backgroundColor: cardBg,
          ...cardGlass,
          ...(tokens.colors.border.cardStyle ?? {}),
          borderRadius: tokens.borderRadius.lg,
          padding: tokens.spacing.md,
          minHeight: 200,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 20,
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 8,
              backgroundColor: "rgba(255,255,255,0.1)",
            }}
          />
          <div style={{ flex: 1 }}>
            <div
              style={{
                height: 18,
                width: "50%",
                backgroundColor: "rgba(255,255,255,0.1)",
                borderRadius: 4,
              }}
            />
            <div
              style={{
                height: 12,
                width: "70%",
                backgroundColor: "rgba(255,255,255,0.06)",
                borderRadius: 4,
                marginTop: 6,
              }}
            />
          </div>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 16,
          }}
        >
          {[1, 2, 3, 4].map((i) => (
            <div key={i}>
              <div
                style={{
                  height: 10,
                  width: "40%",
                  backgroundColor: "rgba(255,255,255,0.06)",
                  borderRadius: 2,
                }}
              />
              <div
                style={{
                  height: 20,
                  width: "60%",
                  backgroundColor: "rgba(255,255,255,0.1)",
                  borderRadius: 4,
                  marginTop: 4,
                }}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Empty state
  if (!spacecraft) {
    return (
      <div
        className={classNames("zendir-orbit-card", "empty", className)}
        role="article"
        aria-label="No orbit data"
        style={{
          backgroundColor: cardBg,
          ...cardGlass,
          ...(tokens.colors.border.cardStyleDashed ?? {}),
          borderRadius: tokens.borderRadius.lg,
          padding: tokens.spacing.lg,
          textAlign: "center",
          color: tokens.colors.text.tertiary,
        }}
      >
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="currentColor"
          style={{ opacity: 0.5, marginBottom: 8 }}
        >
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" />
          <ellipse
            cx="12"
            cy="12"
            rx="10"
            ry="4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
            strokeDasharray="3,3"
          />
        </svg>
        <p style={{ margin: 0, fontSize: tokens.typography.body[2].fontSize }}>
          No orbit data available
        </p>
      </div>
    );
  }

  // Compact view - shows key orbit info in a single row
  if (compact && !expanded) {
    return (
      <article
        className={classNames("zendir-orbit-card", "compact", className)}
        {...handlers}
        aria-label={`Orbit: ${spacecraft.orbitType ?? "Unknown"} (compact)`}
        style={{
          position: "relative",
          overflow: "hidden",
          ...(useGlassMode
            ? glassContainerStyle
            : {
                backgroundColor: cardBg,
                ...cardGlass,
                ...(tokens.colors.border.cardStyle ?? {}),
              }),
          borderRadius: tokens.borderRadius.lg,
          padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
          fontFamily: tokens.typography.fontFamily.primary,
          color: tokens.colors.text.primary,
          fontSize: tokens.typography.fontSize.sm,
          cursor: "pointer",
          transition: `all ${transitionDuration} ease-out`,
          transform: "scale(1)",
        }}
        onMouseEnter={(e) => {
          handlers.onMouseEnter();
          if (!prefersReducedMotion) {
            (e.currentTarget as HTMLElement).style.transform = "scale(1.01)";
            (e.currentTarget as HTMLElement).style.boxShadow =
              `0 4px 12px ${orbitInfo.color}20`;
          }
        }}
        onMouseLeave={(e) => {
          handlers.onMouseLeave();
          (e.currentTarget as HTMLElement).style.transform = "scale(1)";
          (e.currentTarget as HTMLElement).style.boxShadow = "";
        }}
      >
        {/* Glass gradient overlay for bold/minimal themes */}
        {useGlassMode && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.03) 0%, transparent 50%, rgba(0,0,0,0.1) 100%)",
              pointerEvents: "none",
              zIndex: 0,
            }}
          />
        )}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: tokens.spacing.md,
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Left side: Icon + Title + Accent line */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: tokens.spacing.sm,
              flex: 1,
              minWidth: 0,
            }}
          >
            <AstroIcon name="360" size={16} color={orbitInfo.color} />
            <span
              style={{
                fontWeight: tokens.typography.fontWeight.medium,
                color: useGlassMode ? glassAccentColor : undefined,
                textShadow: useGlassMode
                  ? `0 0 16px ${glassAccentMuted}`
                  : undefined,
              }}
            >
              Orbit
            </span>
            {useGlassMode && (
              <div
                style={{
                  flex: 1,
                  height: isBoldVariant ? "2px" : "1px",
                  minWidth: 20,
                  background: `linear-gradient(90deg, ${glassAccentColor} 0%, ${glassAccentMuted} 60%, transparent 100%)`,
                  boxShadow: isBoldVariant
                    ? `0 0 8px ${glassAccentMuted}`
                    : undefined,
                }}
              />
            )}
          </div>

          {/* Right side: Orbit badge + Altitude + Pin */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: tokens.spacing.sm,
              flexShrink: 0,
            }}
          >
            <span
              style={{
                backgroundColor: `${orbitInfo.color}20`,
                color: orbitInfo.color,
                padding: `2px 6px`,
                borderRadius: tokens.borderRadius.sm,
                fontSize: tokens.typography.fontSize.xs,
                fontWeight: tokens.typography.fontWeight.medium,
              }}
            >
              {spacecraft.orbitType ?? "—"}
            </span>
            <span
              style={{
                fontVariantNumeric: "tabular-nums",
                fontFamily: tokens.typography.fontFamily.mono,
                fontSize: tokens.typography.fontSize.xs,
              }}
            >
              {formatAltitude(spacecraft.altitude)}
            </span>
            {isPinned && (
              <Icon
                name="lock"
                size={12}
                color={tokens.colors.accent.primary}
              />
            )}
          </div>
        </div>
      </article>
    );
  }

  const labelStyle: React.CSSProperties = {
    color: tokens.colors.text.secondary, // WCAG AA compliant - 4.5:1 contrast
    fontSize: tokens.typography.fontSize.xs,
    fontWeight: tokens.typography.fontWeight.medium,
  };

  const valueStyle: React.CSSProperties = {
    color: tokens.colors.text.primary,
    fontWeight: tokens.typography.fontWeight.medium,
    fontFamily: tokens.typography.fontFamily.mono,
    fontVariantNumeric: "tabular-nums",
    fontSize: tokens.typography.fontSize.md,
    marginTop: 2,
  };

  return (
    <article
      className={classNames(
        "zendir-orbit-card",
        compact ? "expanded" : "",
        className,
      )}
      {...(compact ? handlers : {})}
      role={compact ? handlers.role : "article"}
      tabIndex={compact ? handlers.tabIndex : undefined}
      aria-expanded={compact ? handlers["aria-expanded"] : undefined}
      aria-label={`Orbit data for ${spacecraft.name ?? spacecraft.id}`}
      style={{
        position: "relative",
        overflow: "hidden",
        ...(useGlassMode
          ? glassContainerStyle
          : {
              backgroundColor: cardBg,
              ...cardGlass,
              ...(tokens.colors.border.cardStyle ?? {}),
            }),
        borderRadius: tokens.borderRadius.lg,
        padding: tokens.spacing.md,
        fontFamily: tokens.typography.fontFamily.primary,
        cursor: compact ? "pointer" : undefined,
        transition: compact ? `all ${transitionDuration} ease-out` : undefined,
        color: tokens.colors.text.primary,
        fontSize: tokens.typography.fontSize.sm,
      }}
    >
      {/* Glass gradient overlay for bold/minimal themes */}
      {useGlassMode && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(135deg, rgba(255,255,255,0.03) 0%, transparent 50%, rgba(0,0,0,0.1) 100%)",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
      )}

      {/* Unified Card Header - handles all theme variants */}
      {/* OrbitCard uses categorical colors (orbit type), not status colors */}
      <CardHeader
        icon="360"
        title={spacecraft.name ?? spacecraft.id ?? "Unknown"}
        subtitle={orbitInfo.description}
        badge={spacecraft.orbitType ?? "ORBIT"}
        badgeColor={orbitInfo.color}
        iconColor={orbitInfo.color}
        hideStatusIndicator
      />

      {/* Orbital Parameters */}
      <section aria-label="Orbital parameters">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: tokens.spacing.md,
          }}
        >
          <div>
            <span style={labelStyle}>Altitude</span>
            <div
              style={{
                ...valueStyle,
                color: safeAccentText(tokens.colors.accent.primary),
              }}
            >
              {formatAltitude(spacecraft.altitude)}
            </div>
          </div>
          <div>
            <span style={labelStyle}>Velocity</span>
            <div style={valueStyle}>
              {spacecraft.velocity
                ? formatVelocity(spacecraft.velocity)
                : computedVelocity
                  ? formatVelocity(computedVelocity)
                  : "-- km/s"}
            </div>
          </div>
          {spacecraft.inclination !== undefined && (
            <div>
              <span style={labelStyle}>Inclination</span>
              <div style={valueStyle}>
                {formatDegrees(spacecraft.inclination)}
              </div>
            </div>
          )}
          {showComputed && computedPeriod !== null && (
            <div>
              <span style={labelStyle}>Period</span>
              <div style={valueStyle}>{formatDuration(computedPeriod)}</div>
            </div>
          )}
        </div>
      </section>

      {/* Current Position */}
      <section
        style={{
          marginTop: tokens.spacing.lg,
          paddingTop: tokens.spacing.lg,
          borderTop: `1px solid ${tokens.colors.border.muted}`,
        }}
        aria-label="Current position"
      >
        <h4
          style={{
            margin: `0 0 ${tokens.spacing.sm} 0`,
            ...labelStyle,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
          </svg>
          Current Position
        </h4>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: tokens.spacing.md,
          }}
        >
          <div>
            <span style={labelStyle}>Latitude</span>
            <div style={valueStyle}>
              {formatCoordinate(spacecraft.latitude, "lat")}
            </div>
          </div>
          <div>
            <span style={labelStyle}>Longitude</span>
            <div style={valueStyle}>
              {formatCoordinate(spacecraft.longitude, "lon")}
            </div>
          </div>
        </div>
      </section>

      {/* Timestamp */}
      {spacecraft.timestamp && (
        <footer
          style={{
            marginTop: tokens.spacing.md,
            fontSize: tokens.typography.fontSize.xs,
            color: tokens.colors.text.tertiary,
            textAlign: "right",
          }}
        >
          <time dateTime={spacecraft.timestamp}>
            {formatTime(spacecraft.timestamp)} UTC
          </time>
        </footer>
      )}
    </article>
  );
});
